// Decoders for the `columnar` rows-stream wire format (see api/app/services/streaming.py).
//
// Each batch arrives as typed byte blobs rather than one msgpack value per cell, so the client
// can copy ids, sort orders and dictionary indices straight into typed arrays instead of
// decoding a million small values one at a time.

/** Column payload mode: a msgpack list of the values themselves. */
export const COLUMNAR_MODE_RAW = 0
/** Column payload mode: `[all strings joined with NUL, positions of nulls]`, decoded in one go. */
export const COLUMNAR_MODE_TEXT_JOINED = 1
/** Column payload mode: little-endian uint16 dictionary indices, 0 = null. */
export const COLUMNAR_MODE_DICT_U16 = 2
/** Column payload mode: little-endian uint32 dictionary indices, 0 = null. */
export const COLUMNAR_MODE_DICT_U32 = 4
/** Column payload mode: the column is null on every row of this batch; there is no payload. */
export const COLUMNAR_MODE_ALL_NULL = 6

const UUID_BYTES = 16
const UUID_LENGTH = 36
const HYPHEN = 45 // '-'
// Char codes of the two hex digits for each byte value.
const HEX_CODES = Array.from({ length: 256 }, (_, i) => {
  const hex = i.toString(16).padStart(2, '0')
  return [hex.charCodeAt(0), hex.charCodeAt(1)]
})
const uuidCharCodes = new Array<number>(UUID_LENGTH)

/**
 * Format the 16 bytes at `offset` as a canonical lower-case hyphenated UUID string.
 *
 * Built from char codes in one call rather than by `+=` concatenation: V8 represents repeated
 * concatenation as rope ("cons") strings, which for a million ids costs ~450 MB of heap and a
 * flatten on first use. `String.fromCharCode` yields a flat string straight away.
 */
export const uuidFromBytes = (bytes: Uint8Array, offset = 0): string => {
  let k = 0
  for (let i = 0; i < UUID_BYTES; i++) {
    if (i === 4 || i === 6 || i === 8 || i === 10) uuidCharCodes[k++] = HYPHEN
    const codes = HEX_CODES[bytes[offset + i]]
    uuidCharCodes[k++] = codes[0]
    uuidCharCodes[k++] = codes[1]
  }
  return String.fromCharCode.apply(null, uuidCharCodes)
}

/** Decode `rowCount` concatenated 16-byte ids into UUID strings. */
export const decodePublicIds = (bytes: Uint8Array, rowCount: number): string[] => {
  const ids = new Array<string>(rowCount)
  for (let i = 0; i < rowCount; i++) {
    ids[i] = uuidFromBytes(bytes, i * UUID_BYTES)
  }
  return ids
}

// The decoder hands back views into its own (possibly unaligned, later reused) chunk buffer, so
// every typed view is taken over a fresh copy. Typed arrays read in platform byte order, which is
// little-endian everywhere the webapp runs — the same order the server packs.
const alignedCopy = (bytes: Uint8Array): ArrayBuffer => bytes.slice().buffer

/** Decode `rowCount` little-endian float64 sort orders. */
export const decodeSortOrders = (bytes: Uint8Array, rowCount: number): Float64Array =>
  new Float64Array(alignedCopy(bytes), 0, rowCount)

/** Decode a dictionary-index payload into the matching typed array. */
export const decodeDictIndices = (mode: number, bytes: Uint8Array, rowCount: number): Uint16Array | Uint32Array => {
  if (mode === COLUMNAR_MODE_DICT_U16) return new Uint16Array(alignedCopy(bytes), 0, rowCount)
  if (mode === COLUMNAR_MODE_DICT_U32) return new Uint32Array(alignedCopy(bytes), 0, rowCount)
  throw new Error(`Unknown columnar dictionary mode ${mode}`)
}

/**
 * Split a NUL-joined text payload back into one value per row. Postgres text can never contain
 * NUL, so the separator is unambiguous; `nulls` lists the rows whose value is null rather than ''.
 */
export const decodeJoinedText = (payload: [string, number[]]): (string | null)[] => {
  const [joined, nulls] = payload
  const values: (string | null)[] = joined.split('\u0000')
  for (let i = 0; i < nulls.length; i++) values[nulls[i]] = null
  return values
}
